import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { saveRelatedContactsQuery } from 'components/contacts/api';
import {
  newGroup,
  addToGroup,
  deleteFromGroup,
  startBulkDelete,
  startDeleteGroups,
  startEditGroups,
  startBulkTagging,
  showCopyRecordsModal,
  showMoveRecordsModal,
} from '../store/actions/properties';
import { retrieveRecords } from '../store/actions/search';
import { toggleAlert } from '../store/actions/ui';
import { toggleView as toggleViewDuplicates } from '../store/actions/duplicates';
import { canFindDuplicates } from '../store/selectors/ui';

import { clickAsLink } from 'helpers/link_helpers';

import ActionsDropdown from 'components/dropdowns/actions_dropdown';

export class PropertiesActionDropdown extends React.Component {
  static propTypes = {
    aggregateView: PropTypes.bool.isRequired,
    canAccessCrm: PropTypes.bool.isRequired,
    canGroup: PropTypes.bool,
    canManageTags: PropTypes.bool.isRequired,
    canTransferRecords: PropTypes.bool.isRequired,
    edgeCrm: PropTypes.bool,
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      })
    ).isRequired,
    properties: PropTypes.array.isRequired,
    retrieveRecords: PropTypes.func.isRequired,
    selectedGroupIds: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    selectedPropertyIds: PropTypes.array.isRequired,
    showFindDuplicatesOption: PropTypes.bool,
    startDeleteGroups: PropTypes.func.isRequired,
    startEditGroups: PropTypes.func.isRequired,
    toggleAlert: PropTypes.func.isRequired,
    onAddToGroup: PropTypes.func.isRequired,
    onBulkDelete: PropTypes.func.isRequired,
    onBulkTagging: PropTypes.func.isRequired,
    onCopyRecords: PropTypes.func.isRequired,
    onCreateGroup: PropTypes.func.isRequired,
    onDeleteFromGroup: PropTypes.func.isRequired,
    onMoveRecords: PropTypes.func.isRequired,
    onToggleFindDuplicates: PropTypes.func.isRequired,
  };

  findSelectedGroups() {
    const { groups, selectedGroupIds } = this.props;
    if (selectedGroupIds.length === 0) {
      return [];
    }

    return groups.filter(group => selectedGroupIds.includes(group.id));
  }

  getProperty(id) {
    const { properties } = this.props;
    return properties.find(property => property.id === id);
  }

  handleAddToGroup = (group) => {
    const { selectedPropertyIds, onAddToGroup } = this.props;
    onAddToGroup(group, selectedPropertyIds || []);
  };

  handleDeleteFromGroup = (group) => {
    const { selectedPropertyIds, onDeleteFromGroup, retrieveRecords } = this.props;
    onDeleteFromGroup(group, selectedPropertyIds);
    retrieveRecords();
  };

  handleStartDeleteGroups = () => {
    this.props.startDeleteGroups(this.findSelectedGroups());
  };

  handleMergeProperties = (e) => {
    const { selectedPropertyIds } = this.props;
    const propertyOne = this.getProperty(selectedPropertyIds[0]);
    clickAsLink(
      window,
      `${propertyOne.mergeUrl}?property_ids=${JSON.stringify(
        selectedPropertyIds
      )}`,
      e
    );
  };

  handleSeeRelatedContacts = (e) => {
    const { selectedPropertyIds, toggleAlert } = this.props;
    saveRelatedContactsQuery({ propertyIds: selectedPropertyIds })
      .then((relatedSearch) => {
        if (this.props.edgeCrm) {
          clickAsLink(
            window,
            `/backoffice/contacts?related_contact_search_id=${relatedSearch.id}`,
            e
          );
        } else {
          const params = `back_to=${encodeURIComponent(window.location.href)}`;
          clickAsLink(
            window,
            `/contacts/related_contacts/${relatedSearch.id}?${params}`,
            e
          );
        }
      })
      .catch(() => {
        toggleAlert('An error occurred when searching related contacts.', true);
      });
  };

  handleStartEditGroups = () => {
    this.props.startEditGroups(this.findSelectedGroups());
  };

  handleFindDuplicates = () => {
    const { onToggleFindDuplicates, retrieveRecords } = this.props;
    onToggleFindDuplicates(true);
    retrieveRecords();
  };

  isDisabled = () => {
    const { selectedGroupIds, selectedPropertyIds, showFindDuplicatesOption } =
      this.props;

    if (showFindDuplicatesOption || selectedPropertyIds.length > 0)
      return false;
    return selectedGroupIds.length === 0 || selectedGroupIds[0] === 'ungrouped';
  };

  render() {
    const props = { ...this.props };
    const selectedPropertyIds = props.selectedPropertyIds;
    const showFindDuplicatesOption = props.showFindDuplicatesOption;

    if (props.edgeCrm) {
      // If Egge CRM is enabled, we can't move or copy records to another database
      delete props.onMoveRecords;
      delete props.onCopyRecords;
    }

    // delete props not needed by ActionsDropdown below
    delete props.edgeCrm;
    delete props.selectedPropertyIds;
    delete props.showFindDuplicatesOption;

    const selectedGroups = this.findSelectedGroups();

    return (
      <ActionsDropdown
        {...props}
        canFindDuplicates={showFindDuplicatesOption}
        disabled={this.isDisabled()}
        isProperty
        selectedGroupIds={selectedGroups.map(group => group.id)}
        selectedIds={selectedPropertyIds}
        onAddToGroup={this.handleAddToGroup}
        onDeleteFromGroup={this.handleDeleteFromGroup}
        onDeleteGroup={this.handleStartDeleteGroups}
        onFindDuplicates={this.handleFindDuplicates}
        onMergeRecords={this.handleMergeProperties}
        onSeeRelatedContacts={this.handleSeeRelatedContacts}
        onUpdateGroup={this.handleStartEditGroups}
      />
    );
  }
}

const mapStateToProps = state => ({
  aggregateView: state.static.aggregateView,
  canAccessCrm: state.ui.canAccessCrm,
  canDelete: state.ui.canDelete,
  canGroup: state.ui.canGroup,
  canManageTags: state.ui.canManageTags,
  canTransferRecords: state.ui.canTransferRecords,
  edgeCrm: state.features.edgeCrm,
  groups: state.properties.groups,
  properties: state.search.properties,
  selectedGroupIds: state.query.researchGroupIds,
  selectedPropertyIds: state.selectList.selectedPropertyIds,
  showFindDuplicatesOption: canFindDuplicates(state),
});

const mapDispatchToProps = {
  toggleAlert,
  retrieveRecords,
  startDeleteGroups,
  startEditGroups,

  onAddToGroup: addToGroup,
  onDeleteFromGroup: deleteFromGroup,
  onCreateGroup: newGroup,
  onBulkDelete: startBulkDelete,
  onBulkTagging: startBulkTagging,
  onCopyRecords: showCopyRecordsModal,
  onMoveRecords: showMoveRecordsModal,
  onToggleFindDuplicates: toggleViewDuplicates,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PropertiesActionDropdown);
